import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Host } from "@/types/database";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { type PermitValidationResult } from "@/lib/bcRegistry";
import {
  notifyHostRegistrationStatus,
  notifyManagersNewHost,
} from "@/lib/notifications";
import { sendHostRegistrationStatusEmail } from "@/lib/email";
import { logActivity } from "@/lib/activityLogger";
import { trackEvent } from "@/lib/analytics";

// ─── Check whether a Stratakey account exists for a given email ───────────────
export type EmailCheckResult =
  | { found: true;  userId: string; fullName: string | null; email: string }
  | { found: false; userId: null;   fullName: null;          email: string };

export function useCheckHostEmail(email: string) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(email.trim());

  return useQuery<EmailCheckResult>({
    queryKey: ["check_host_email", email.toLowerCase().trim()],
    enabled: isValidEmail,
    staleTime: 30_000,
    queryFn: async (): Promise<EmailCheckResult> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .ilike("email", email.trim())
        .maybeSingle();
      if (error) throw error;
      if (data) {
        return { found: true, userId: data.id, fullName: data.full_name, email: data.email };
      }
      return { found: false, userId: null, fullName: null, email: email.trim() };
    },
  });
}

export function useHosts() {
  const { activeOrgId } = useAuth();

  return useQuery({
    queryKey: ["hosts", activeOrgId],
    enabled: !!activeOrgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hosts")
        .select(`
          *,
          profile:profiles!hosts_user_id_fkey(*),
          unit:units!hosts_unit_id_fkey(*)
        `)
        .eq("organization_id", activeOrgId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Host[];
    },
  });
}

export function useApproveHost() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async (hostId: string) => {
      // Fetch the host first so we have user_id, email, name, unit, and org for notifications
      const { data: hostRecord } = await supabase
        .from("hosts")
        .select("user_id, unit:units!hosts_unit_id_fkey(unit_number), profile:profiles!hosts_user_id_fkey(full_name, email), organization:organizations!hosts_organization_id_fkey(name)")
        .eq("id", hostId)
        .single();

      const { data, error } = await supabase
        .from("hosts")
        .update({ status: "approved" })
        .eq("id", hostId)
        .select()
        .single();
      if (error) throw error;

      await supabase.from("audit_logs").insert({
        organization_id: activeOrgId,
        action: "Host approved",
        entity: `Host ${hostId}`,
        detail: "Host registration approved",
        user_name: user?.fullName ?? "Admin",
      });

      // Notify the host (fire-and-forget)
      if (hostRecord?.user_id && activeOrgId) {
        const unitNumber = (hostRecord.unit as any)?.unit_number ?? "—";
        const hostEmail  = (hostRecord.profile as any)?.email as string | undefined;
        const hostName   = (hostRecord.profile as any)?.full_name ?? "Host";
        const orgName    = (hostRecord.organization as any)?.name ?? "Your Organization";

        notifyHostRegistrationStatus(hostRecord.user_id, true, unitNumber, activeOrgId)
          .catch((e) => console.warn("[notify] Host approval notification failed:", e));

        if (hostEmail) {
          sendHostRegistrationStatusEmail({
            hostEmail,
            hostName,
            unitNumber,
            orgName,
            approved: true,
            dashboardUrl: `${window.location.origin}/dashboard`,
          }).catch((e) => console.warn("[email] Host approval email failed:", e));
        }
      }

      return data;
    },
    onSuccess: (data, hostId) => {
      queryClient.invalidateQueries({ queryKey: ["hosts", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["audit_logs", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["activity_feed", activeOrgId] });
      toast({ title: "Host approved", description: "The host has been approved successfully." });
      trackEvent("host_approved", { org_id: activeOrgId ?? undefined });
      if (activeOrgId) {
        logActivity({ orgId: activeOrgId, userId: user?.id, actionType: "host_approved", entityType: "host", entityId: hostId });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useCreateHost() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async (host: {
      user_id: string;
      unit_id: string;
      registration_number?: string;
      night_limit?: number;
      host_type?: string;
      business_licence_number?: string;
      business_licence_status?: string;
    }): Promise<{ host: Host; validation: PermitValidationResult | null }> => {
      if (!activeOrgId) throw new Error("No active organization");

      // 1. Insert host as "pending"
      const { data, error } = await supabase
        .from("hosts")
        .insert({ ...host, organization_id: activeOrgId, status: "pending" })
        .select("*, unit:units!hosts_unit_id_fkey(*), profile:profiles!hosts_user_id_fkey(*)")
        .single();
      if (error) throw error;

      // 2. Ensure the host user has a user_assignments row for this org so that
      //    activeOrgId is populated in AuthContext (needed for amenities, messages,
      //    maintenance, etc. which all query by activeOrgId).
      //
      //    user_assignments has an expression-based unique index on
      //    (user_id, org_id, COALESCE(unit_id, sentinel_uuid)) so we cannot use
      //    upsert with onConflict. Instead: plain insert, ignore duplicate errors.
      const { error: assignError } = await supabase
        .from("user_assignments")
        .insert({
          user_id: host.user_id,
          org_id: activeOrgId,
          role: "host",
          unit_id: host.unit_id,
          permissions: {},
        });
      if (assignError) {
        const isDuplicate =
          assignError.message.toLowerCase().includes("duplicate") ||
          assignError.message.toLowerCase().includes("unique") ||
          assignError.code === "23505";
        if (!isDuplicate) {
          // Log unexpected errors but don't fail — host record is the source of truth
          console.warn("[useCreateHost] user_assignments insert failed:", assignError.message);
        }
      }

      // Auto-validate registration number against BC Registry if provided
      let validation: PermitValidationResult | null = null;
      if (host.registration_number) {
        try {
          const { validatePermit } = await import("@/lib/bcRegistry");
          validation = await validatePermit(host.registration_number);
          await supabase
            .from("hosts")
            .update({
              registry_status: validation.status,
              registry_validated_at: new Date().toISOString(),
              ...(validation.expiresAt ? { expires_at: validation.expiresAt } : {}),
            })
            .eq("id", data.id);
        } catch (e) {
          console.warn("[useCreateHost] Registry validation failed:", e);
        }
      }

      // 3. Log creation
      await supabase.from("audit_logs").insert({
        organization_id: activeOrgId,
        action: "Host registered",
        entity: `Host ${data.id}`,
        detail: `Registration: ${host.registration_number || "N/A"}`,
        user_name: user?.fullName ?? "Admin",
      });

      // 4. Notify org managers about new registration (fire-and-forget)
      const unitNumber = (data.unit as any)?.unit_number ?? "—";
      const hostName = (data.profile as any)?.full_name ?? "Unknown";
      notifyManagersNewHost(activeOrgId, unitNumber, hostName, data.id)
        .catch((e) => console.warn("[notify] Manager new-host notification failed:", e));

      return { host: data as Host, validation };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["hosts", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["audit_logs", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["activity_feed", activeOrgId] });
      if (activeOrgId) {
        const unitNum = (result.host.unit as any)?.unit_number;
        const hostName = (result.host.profile as any)?.full_name;
        logActivity({
          orgId: activeOrgId, userId: user?.id, actionType: "host_registered",
          entityType: "host", entityId: result.host.id,
          metadata: { unit_number: unitNum, host_name: hostName },
        });
      }

      trackEvent("host_registered", { org_id: activeOrgId ?? undefined, has_registration_number: !!result.host.registration_number });
      // If registry auto-verified successfully, also fire owner_verified
      if (result.validation?.isValid) {
        trackEvent("owner_verified", {
          org_id: activeOrgId ?? undefined,
          registration_number: result.validation.identifier ?? undefined,
          source: "auto_on_create",
        });
      }
      toast({
        title: "Host added",
        description: result.host.registration_number
          ? "Host registered successfully."
          : "Host registered successfully.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useRejectHost() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async (hostId: string) => {
      // Fetch host first to get user_id, email, name, unit, and org for notifications
      const { data: hostRecord } = await supabase
        .from("hosts")
        .select("user_id, unit:units!hosts_unit_id_fkey(unit_number), profile:profiles!hosts_user_id_fkey(full_name, email), organization:organizations!hosts_organization_id_fkey(name)")
        .eq("id", hostId)
        .single();

      const { data, error } = await supabase
        .from("hosts")
        .update({ status: "rejected" })
        .eq("id", hostId)
        .select()
        .single();
      if (error) throw error;

      await supabase.from("audit_logs").insert({
        organization_id: activeOrgId,
        action: "Host rejected",
        entity: `Host ${hostId}`,
        detail: "Host registration rejected",
        user_name: user?.fullName ?? "Admin",
      });

      // Notify the host (fire-and-forget)
      if (hostRecord?.user_id && activeOrgId) {
        const unitNumber = (hostRecord.unit as any)?.unit_number ?? "—";
        const hostEmail  = (hostRecord.profile as any)?.email as string | undefined;
        const hostName   = (hostRecord.profile as any)?.full_name ?? "Host";
        const orgName    = (hostRecord.organization as any)?.name ?? "Your Organization";

        notifyHostRegistrationStatus(hostRecord.user_id, false, unitNumber, activeOrgId)
          .catch((e) => console.warn("[notify] Host rejection notification failed:", e));

        if (hostEmail) {
          sendHostRegistrationStatusEmail({
            hostEmail,
            hostName,
            unitNumber,
            orgName,
            approved: false,
            dashboardUrl: `${window.location.origin}/dashboard`,
          }).catch((e) => console.warn("[email] Host rejection email failed:", e));
        }
      }

      return data;
    },
    onSuccess: (data, hostId) => {
      queryClient.invalidateQueries({ queryKey: ["hosts", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["audit_logs", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["activity_feed", activeOrgId] });
      toast({ title: "Host rejected", description: "The host has been rejected." });
      trackEvent("host_rejected", { org_id: activeOrgId ?? undefined });
      if (activeOrgId) {
        logActivity({ orgId: activeOrgId, userId: user?.id, actionType: "host_rejected", entityType: "host", entityId: hostId });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useVerifyRegistrationDoc() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { activeOrgId, user } = useAuth();

  return useMutation({
    mutationFn: async ({
      hostId,
      registrationNumber,
      certFile,
    }: {
      hostId: string;
      registrationNumber: string;
      certFile: File;
    }) => {
      // Convert file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(certFile);
      });

      // Call Claude Vision edge function
      const { data, error } = await supabase.functions.invoke("verify-registration-doc", {
        body: {
          registrationNumber: registrationNumber.trim().toUpperCase(),
          documentBase64: base64,
          mimeType: certFile.type || "image/jpeg",
        },
      });
      if (error) throw new Error(error.message ?? "Verification request failed");

      const result = data as {
        matched: boolean;
        extractedNumber: string | null;
        status: "verified" | "mismatch" | "unreadable" | "error";
        message: string;
      };

      if (!result.matched) throw new Error(result.message || "Document verification failed");

      // Update host registry fields
      const { error: updateError } = await supabase
        .from("hosts")
        .update({
          registration_number: registrationNumber.trim().toUpperCase(),
          registry_status: "active",
          registry_validated_at: new Date().toISOString(),
        })
        .eq("id", hostId);
      if (updateError) throw updateError;

      // Upload certificate + insert document record
      if (activeOrgId) {
        const filePath = `documents/${hostId}/${Date.now()}_${certFile.name}`;
        const { error: storageError } = await supabase.storage
          .from("host-documents")
          .upload(filePath, certFile);
        if (!storageError) {
          const { data: urlData } = supabase.storage.from("host-documents").getPublicUrl(filePath);
          await supabase.from("documents").insert({
            host_id: hostId,
            organization_id: activeOrgId,
            name: "BC STR Registration Certificate",
            file_name: certFile.name,
            file_url: urlData.publicUrl,
            status: "approved",
          });
        }

        await supabase.from("audit_logs").insert({
          organization_id: activeOrgId,
          action: "BC Registration document verified",
          entity: `Host ${hostId}`,
          detail: `Document verification passed: ${registrationNumber.trim().toUpperCase()}`,
          user_name: user?.fullName ?? "Host",
        });
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hosts", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["documents", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["registration_cert", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["audit_logs", activeOrgId] });
      toast({
        title: "Registration verified",
        description: "Your BC STR registration certificate has been verified successfully.",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Verification failed", description: error.message, variant: "destructive" });
    },
  });
}

export function useValidateRegistration() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async ({
      hostId,
      registrationNumber,
    }: {
      hostId: string;
      registrationNumber: string;
    }) => {
      // 1. Call the BC Registry validate Edge Function
      const { validatePermit } = await import("@/lib/bcRegistry");
      const validation = await validatePermit(registrationNumber.trim().toUpperCase());

      // 2. Persist registration number + validation result on the host record
      const { data, error } = await supabase
        .from("hosts")
        .update({
          registration_number: registrationNumber.trim().toUpperCase(),
          registry_status: validation.status,
          registry_validated_at: new Date().toISOString(),
          // Auto-update expiry from registry if available
          ...(validation.expiresAt ? { expires_at: validation.expiresAt } : {}),
        })
        .eq("id", hostId)
        .select()
        .single();
      if (error) throw error;

      // 3. Audit log
      if (activeOrgId) {
        await supabase.from("audit_logs").insert({
          organization_id: activeOrgId,
          action: "BC Registry validated",
          entity: `Host ${hostId}`,
          detail: `Registration ${registrationNumber.trim().toUpperCase()} — status: ${validation.status}${validation.message ? ` (${validation.message})` : ""}`,
        });
      }

      return { host: data, validation };
    },
    onSuccess: ({ validation }) => {
      queryClient.invalidateQueries({ queryKey: ["hosts", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["audit_logs", activeOrgId] });

      const statusMessages: Record<string, string> = {
        active:    "Registration is active in the BC STR Registry.",
        expired:   "Registration found but has expired — please renew.",
        suspended: "Registration is suspended — contact BC Registry.",
        not_found: "Registration number not found in the BC STR Registry.",
        error:     "Could not reach the BC Registry — try again later.",
      };

      const isActive = validation.status === "active";
      toast({
        title: isActive ? "Registration verified" : "Validation complete",
        description: statusMessages[validation.status] ?? validation.message ?? "",
        variant: isActive ? "default" : "destructive",
      });
    },
    onError: (error: Error) => {
      toast({ title: "Validation failed", description: error.message, variant: "destructive" });
    },
  });
}
